export type McpComment = {
  id: string;
  author: string;
  postedAt: string;
  content: string;
  likes: number;
  reply?: {
    author: string;
    postedAt: string;
    content: string;
  };
};

export type McpEntry = {
  name: string;
  slug: string;
  icon: string;
  description: string;
  category: string;
  creator: string;
  stars: number;
  downloads: number;
  updatedAt: string;
  createdAt: string;
  weeklyDownloads: number;
  activeUsersApprox: number;
  lastStatsUpdate: string;
  downloadUrl: string;
  setupGuideUrl: string;
  /** If set, registry detail shows only this hosted MCP URL (no Download / Setup Guide). */
  linkOnlyUrl?: string;
  /** Number of live endpoints/tools this MCP exposes. */
  endpointCount?: number;
  /** "header" | "oauth" | "none" — if omitted treated as "none" (public) */
  authType?: "header" | "oauth" | "none";
  /** Human-readable label for the credential, e.g. "Notion Integration Token" */
  authLabel?: string;
  /** Instructions shown in the connect modal */
  authInstructions?: string;
  /** HTTP header name to pass the credential in, e.g. "Authorization" */
  credentialHeader?: string;
  /** Env-var / key name, e.g. "NOTION_TOKEN" */
  credentialKey?: string;
  features: string[];
  quickStartSteps: string[];
  comments: McpComment[];
};

export const MCP_REGISTRY: McpEntry[] = [
  {
    name: "Notion MCP",
    slug: "notion-mcp",
    icon: "",
    description: "Official Notion API for reading and writing Notion pages and databases",
    category: "productivity",
    creator: "@apitomcp",
    stars: 0,
    downloads: 0,
    updatedAt: "today",
    createdAt: "May 4, 2026",
    weeklyDownloads: 0,
    activeUsersApprox: 0,
    lastStatsUpdate: "May 11, 2026",
    downloadUrl: "/mcp/notion-mcp",
    setupGuideUrl: "https://developers.notion.com/",
    linkOnlyUrl: "/mcp/notion-mcp",
    authType: "header",
    authLabel: "Notion Integration Token",
    credentialHeader: "Authorization",
    credentialKey: "NOTION_TOKEN",
    authInstructions:
      "Create a Notion integration at https://www.notion.so/my-integrations. Copy the Internal Integration Token (starts with 'secret_'). Add it as the Authorization header value: 'Bearer YOUR_TOKEN'.",
    features: [
      "Query and manage Notion databases",
      "Create, retrieve, and update pages",
      "Read, append, update, and archive blocks",
      "Search pages and databases by title",
      "List users and read workspace comments",
    ],
    quickStartSteps: [],
    comments: [],
  },
  {
    name: "Linear MCP",
    slug: "linear-mcp",
    icon: "",
    description: "Linear API operations as separate endpoints for MCP",
    category: "productivity",
    creator: "@apitomcp",
    stars: 98,
    downloads: 890,
    updatedAt: "today",
    createdAt: "Apr 20, 2026",
    weeklyDownloads: 44,
    activeUsersApprox: 120,
    lastStatsUpdate: "May 11, 2026",
    downloadUrl: "/mcp/linear-mcp",
    setupGuideUrl: "https://developers.linear.app/docs/graphql/working-with-the-graphql-api",
    linkOnlyUrl: "/mcp/linear-mcp",
    authType: "header",
    authLabel: "Linear API Key",
    credentialHeader: "Authorization",
    credentialKey: "LINEAR_API_KEY",
    authInstructions:
      "Go to Linear → Settings → API → Personal API keys. Create a new key and copy it. Add it as the Authorization header value: 'Bearer YOUR_API_KEY'.",
    features: [
      "Get all issues",
      "Create, update, get, and delete issues",
      "Get all teams and projects",
      "Create comments on issues",
      "Search issues by query",
      "Get the authenticated user",
    ],
    quickStartSteps: [],
    comments: [],
  },
  {
    name: "PokéAPI MCP",
    slug: "pokeapi-mcp",
    icon: "",
    description: "All the Pokémon data you'll ever need in one place",
    category: "games",
    creator: "@apitomcp",
    stars: 42,
    downloads: 128,
    updatedAt: "today",
    createdAt: "May 10, 2026",
    weeklyDownloads: 32,
    activeUsersApprox: 80,
    lastStatsUpdate: "May 10, 2026",
    downloadUrl: "/mcp/pokeapi-mcp",
    setupGuideUrl: "https://pokeapi.co/docs/v2",
    linkOnlyUrl: "/mcp/pokeapi-mcp",
    features: [
      "List Pokémon (paginated with limit and offset)",
      "Get a Pokémon by ID or name",
      "Look up Pokémon types (e.g. fire, water, grass)",
      "Get ability details",
      "Get generation details",
    ],
    quickStartSteps: [],
    comments: [],
  },
  {
    name: "NASA APOD MCP",
    slug: "nasa-apod-mcp",
    icon: "",
    description: "Astronomy Picture of the Day from NASA",
    category: "science",
    creator: "@apitomcp",
    stars: 31,
    downloads: 96,
    updatedAt: "today",
    createdAt: "May 11, 2026",
    weeklyDownloads: 24,
    activeUsersApprox: 55,
    lastStatsUpdate: "May 11, 2026",
    downloadUrl: "/mcp/nasa-apod-mcp",
    setupGuideUrl: "https://api.nasa.gov/",
    linkOnlyUrl: "/mcp/nasa-apod-mcp",
    features: [
      "Get Astronomy Picture of the Day",
      "Filter by date (YYYY-MM-DD)",
      "Fetch APOD for a date range (start_date and end_date)",
      "Return a random set of images (count)",
      "Works with NASA DEMO_KEY for testing",
    ],
    quickStartSteps: [],
    comments: [],
  },
  {
    name: "Coinbase Public API MCP",
    slug: "coinbase-mcp",
    icon: "",
    description: "Get cryptocurrency prices and market data (no auth needed)",
    category: "finance",
    creator: "@apitomcp",
    stars: 38,
    downloads: 112,
    updatedAt: "today",
    createdAt: "May 11, 2026",
    weeklyDownloads: 28,
    activeUsersApprox: 62,
    lastStatsUpdate: "May 11, 2026",
    downloadUrl: "/mcp/coinbase-mcp",
    setupGuideUrl: "https://api.coinbase.com/v2",
    linkOnlyUrl: "/mcp/coinbase-mcp",
    features: [
      "List all known currencies",
      "Get current exchange rates (base currency, default USD)",
      "Get spot price for a currency pair (e.g. BTC-USD)",
      "Get buy price for a currency pair",
      "Get sell price for a currency pair",
    ],
    quickStartSteps: [],
    comments: [],
  },
  {
    name: "Random User Generator API MCP",
    slug: "random-user-mcp",
    icon: "",
    description: "Generate random user data for testing",
    category: "development",
    creator: "@apitomcp",
    stars: 27,
    downloads: 84,
    updatedAt: "today",
    createdAt: "May 11, 2026",
    weeklyDownloads: 19,
    activeUsersApprox: 48,
    lastStatsUpdate: "May 11, 2026",
    downloadUrl: "/mcp/random-user-mcp",
    setupGuideUrl: "https://randomuser.me/documentation",
    linkOnlyUrl: "/mcp/random-user-mcp",
    features: [
      "Generate random users (results count, default 1)",
      "Filter by gender (male or female)",
      "Filter by nationality (e.g. US, GB, FR, IN)",
      "Include only specific fields (inc, comma separated)",
      "Exclude specific fields (exc, comma separated)",
    ],
    quickStartSteps: [],
    comments: [],
  },
  {
    name: "JokeAPI MCP",
    slug: "jokeapi-mcp",
    icon: "",
    description: "Free jokes API for your projects",
    category: "entertainment",
    creator: "@apitomcp",
    stars: 29,
    downloads: 73,
    updatedAt: "today",
    createdAt: "May 11, 2026",
    weeklyDownloads: 17,
    activeUsersApprox: 41,
    lastStatsUpdate: "May 11, 2026",
    downloadUrl: "/mcp/jokeapi-mcp",
    setupGuideUrl: "https://v2.jokeapi.dev/",
    linkOnlyUrl: "/mcp/jokeapi-mcp",
    features: [
      "Get a random joke by category (Programming, Misc, Dark, Pun, and more)",
      "Filter joke type (single or twopart)",
      "Search jokes with a contains string",
      "Enable safe-mode to avoid offensive jokes",
      "Get API info and statistics",
    ],
    quickStartSteps: [],
    comments: [],
  },
  {
    name: "Stripe MCP",
    slug: "stripe-mcp",
    icon: "",
    description: "Accept payments, manage customers, and handle billing via Claude",
    category: "finance",
    creator: "@apitomcp",
    stars: 0,
    downloads: 0,
    updatedAt: "today",
    createdAt: "May 27, 2026",
    weeklyDownloads: 0,
    activeUsersApprox: 0,
    lastStatsUpdate: "May 27, 2026",
    downloadUrl: "/mcp/stripe-mcp",
    setupGuideUrl: "https://stripe.com/docs/api",
    linkOnlyUrl: "/mcp/stripe-mcp",
    authType: "header",
    authLabel: "Stripe Secret Key",
    credentialHeader: "Authorization",
    credentialKey: "STRIPE_SECRET_KEY",
    authInstructions:
      "Go to dashboard.stripe.com → Developers → API keys. Copy your Secret key (starts with 'sk_test_' for test mode). Add it as the Authorization header: 'Bearer sk_test_YOUR_KEY'.",
    features: [
      "List and retrieve customers",
      "Create and manage payment intents",
      "List charges and refunds",
      "Retrieve and update subscriptions",
      "List products and prices",
      "Retrieve balance and transactions",
    ],
    quickStartSteps: [],
    comments: [],
  },
  {
    name: "GitHub MCP",
    slug: "github-mcp",
    icon: "",
    description: "Manage repos, issues, pull requests, and code via Claude",
    category: "development",
    creator: "@apitomcp",
    stars: 0,
    downloads: 0,
    updatedAt: "today",
    createdAt: "May 27, 2026",
    weeklyDownloads: 0,
    activeUsersApprox: 0,
    lastStatsUpdate: "May 27, 2026",
    downloadUrl: "/mcp/github-mcp",
    setupGuideUrl: "https://docs.github.com/en/rest",
    linkOnlyUrl: "/mcp/github-mcp",
    authType: "header",
    authLabel: "GitHub Personal Access Token",
    credentialHeader: "Authorization",
    credentialKey: "GITHUB_TOKEN",
    authInstructions:
      "Go to github.com → Settings → Developer settings → Personal access tokens → Tokens (classic). Generate a new token with 'repo' scope. Add it as the Authorization header: 'Bearer ghp_YOUR_TOKEN'.",
    features: [
      "List and search repositories",
      "Create, update, and close issues",
      "List and review pull requests",
      "Read and write file contents",
      "List commits and branches",
      "Search code across repositories",
    ],
    quickStartSteps: [],
    comments: [],
  },
  {
    name: "Slack MCP",
    slug: "slack-mcp",
    icon: "",
    description: "Read channels, send messages, and search Slack via Claude",
    category: "productivity",
    creator: "@apitomcp",
    stars: 0,
    downloads: 0,
    updatedAt: "today",
    createdAt: "May 27, 2026",
    weeklyDownloads: 0,
    activeUsersApprox: 0,
    lastStatsUpdate: "May 27, 2026",
    downloadUrl: "/mcp/slack-mcp",
    setupGuideUrl: "https://api.slack.com/",
    linkOnlyUrl: "/mcp/slack-mcp",
    authType: "header",
    authLabel: "Slack Bot Token",
    credentialHeader: "Authorization",
    credentialKey: "SLACK_BOT_TOKEN",
    authInstructions:
      "Go to api.slack.com/apps → Create New App → OAuth & Permissions. Add Bot Token Scopes: channels:read, chat:write, search:read. Install to workspace and copy the Bot User OAuth Token (starts with 'xoxb-'). Add it as Authorization: 'Bearer xoxb-YOUR_TOKEN'.",
    features: [
      "List and join channels",
      "Post and schedule messages",
      "Search messages across workspace",
      "List workspace users and profiles",
      "Read channel history and threads",
      "Upload files and snippets",
    ],
    quickStartSteps: [],
    comments: [],
  },
];

export const POPULAR_MCP_SLUGS = ["notion-mcp", "pokeapi-mcp", "nasa-apod-mcp"];

export function getMcpBySlug(slug: string) {
  return MCP_REGISTRY.find((mcp) => mcp.slug === slug);
}
